import type { UsageInfo } from "@/types";

// Model-aware client-side cost estimator. Used when the backend's cost_usd
// is missing or zero (current dev backends don't always price tokens).
//
// Prices are $ per 1M tokens. Add new models here as they ship. If a model
// isn't in the table we fall through to GPT-5-mini rates (the foreman default
// per planner_config.py).

interface Price {
  input: number;
  output: number;
  cacheWrite?: number;
  cacheRead?: number;
}

const PRICING: Record<string, Price> = {
  // OpenAI
  "gpt-5-mini":            { input: 0.25, output: 2.00, cacheRead: 0.025 },
  "gpt-5":                 { input: 1.25, output: 10.00, cacheRead: 0.125 },
  "gpt-4o":                { input: 2.50, output: 10.00, cacheRead: 1.25 },
  "gpt-4o-mini":           { input: 0.15, output: 0.60, cacheRead: 0.075 },
  // Anthropic
  "claude-sonnet-4-6":     { input: 3.00, output: 15.00, cacheWrite: 3.75, cacheRead: 0.30 },
  "claude-sonnet-4-7":     { input: 3.00, output: 15.00, cacheWrite: 3.75, cacheRead: 0.30 },
  "claude-haiku-4-5":      { input: 1.00, output: 5.00, cacheWrite: 1.25, cacheRead: 0.10 },
  "claude-opus-4-7":       { input: 15.00, output: 75.00, cacheWrite: 18.75, cacheRead: 1.50 },
};

const DEFAULT_MODEL = "gpt-5-mini";

function priceFor(model: string | undefined): Price {
  if (!model) return PRICING[DEFAULT_MODEL];
  // Exact match first
  if (PRICING[model]) return PRICING[model];
  // Prefix match (e.g. "claude-sonnet-4-6-20260101" → "claude-sonnet-4-6")
  for (const key of Object.keys(PRICING)) {
    if (model.startsWith(key)) return PRICING[key];
  }
  return PRICING[DEFAULT_MODEL];
}

export function estimateCost(usage: UsageInfo | undefined): number {
  if (!usage) return 0;
  const p = priceFor(usage.model);
  const inp = (usage.input_tokens ?? 0) * p.input;
  const out = (usage.output_tokens ?? 0) * p.output;
  const cw = (usage.cache_creation_tokens ?? 0) * (p.cacheWrite ?? p.input * 1.25);
  const cr = (usage.cache_read_tokens ?? 0) * (p.cacheRead ?? p.input * 0.10);
  return (inp + out + cw + cr) / 1_000_000;
}

/** Backend cost if non-zero, else falls back to model-aware client estimate. */
export function effectiveCost(usage: UsageInfo | undefined): number {
  const backend = usage?.cost_usd ?? 0;
  if (backend > 0) return backend;
  return estimateCost(usage);
}

// ── Currency display ────────────────────────────────────────────────
// All LLM API pricing is USD-denominated upstream, but Foreman is a UK
// product and Owen reads costs in pence to feel-out per-message cost
// (e.g. "1.2p" is more legible than "$0.0015"). Fixed FX rate for UI
// display only — never used for billing, never round-trips through
// the backend. Update annually OR wire to a live FX endpoint if/when
// daily fluctuation matters (it doesn't for sub-pence display).
const USD_TO_GBP = 0.79;

/** Format a USD cost as a UK-readable £-denominated string. Always
 * prefixes with £. Sub-£1 amounts ALWAYS show 4 decimals so you can
 * compare per-message costs without precision loss. ≥£1 collapses to
 * 2dp because pence-level precision stops mattering at that scale.
 *
 *   $0.0072 → "£0.0057"
 *   $0.045  → "£0.0356"
 *   $0.42   → "£0.3318"
 *   $1.27   → "£1.00"
 *   $0.0008 → "£0.0006"
 *
 * Owen 2026-05-24: "the costs are only like two additional places ...
 * rather have it like four after the decimal places". */
export function formatCostGBP(usd: number): string {
  if (!Number.isFinite(usd) || usd <= 0) return "£0.0000";
  const gbp = usd * USD_TO_GBP;
  if (gbp >= 1) return `£${gbp.toFixed(2)}`;
  return `£${gbp.toFixed(4)}`;
}
